/**
 * Who this comment names — and, where naming somebody lets their firm in, the sentence that
 * says so before the naming happens (`MCK-20260521-008`; the audience rule is
 * `KLQ-20260821-001` §F, the same rule the assignee picker obeys a few hundred lines up in
 * `TaskDetail.tsx`).
 *
 * Three things carry this file:
 *
 * 1. **The candidates are the members the screen already has.** They arrive as a prop from
 *    `LoadedTaskDetail`, which holds the one `membersQueryOptions` result this screen runs,
 *    and every narrowing below happens in memory. There is no mention-search endpoint here
 *    and there must not be one: an autocomplete that asked the server per keystroke would
 *    multiply one composer into a request per character typed, for every person with the task
 *    open.
 *
 * 2. **It is `ChipSelect`, not a third listbox.** `ChipSelect`'s own header states the one
 *    condition under which a second hand-written popup may exist — a search field *inside*
 *    the popup — and `ListboxPopup` is the file that was written when that condition was
 *    finally met. A mention set is neither: it is values held inside one control, which is
 *    exactly what `ChipSelect` is for. Its picker is an adder rather than a state, and that
 *    is what leaves room for point 3.
 *
 * 3. **External is said in words, on the option itself.** A native `<option>` carries text and
 *    nothing else — no badge, no icon, no class that survives the platform's own menu — so the
 *    mark has to be *in* the label or it is not there at all on the surface where the choice
 *    is actually made. That is the right answer anyway: the marker is a word, it reads the
 *    same to a screen reader as to an eye, and it is never colour alone. The same label is
 *    used for the chip once the person is held, so a set that is half external still says so
 *    after the menu has closed.
 *
 * What this file deliberately does **not** know is the audience. `admissionFor` in
 * `TaskDetail.tsx` also checks `GET /tasks/{id}/audience` before it interposes its sentence,
 * because assigning to somebody whose firm is *already* in admits nobody. A mention's marker
 * is a fact about the candidate, not about the task: an engineer from another firm is an
 * engineer from another firm whether or not their firm was let in this morning. So the test
 * here is the narrow one — a claimed company that is not the owning workspace — and the
 * confirmation states the grant in those terms.
 */

import { useState } from 'react';

import { Button, ChipSelect } from '@/components';
import type { ProjectMember } from '@/features/people';
import type { Translate } from '@/lib/i18n';

import styles from './Tasks.module.css';

/** A pick that has been made but not yet performed — the interposed sentence's subject. */
interface PendingMention {
  readonly userId: string;
  readonly personName: string;
  readonly firmName: string;
}

export interface MentionPickerProps {
  /** The project's members, from the one members query this screen already ran. */
  readonly members: readonly ProjectMember[];
  /**
   * The workspace that owns the project, or `null` when this caller cannot know it — an
   * external caller sees no owner, and a screen that cannot tell inside from outside marks
   * nobody rather than marking the wrong person (§F: a wrong sentence about access is worse
   * than none).
   */
  readonly owningWorkspaceId: string | null;
  /** The user ids held so far, in the order they were added. */
  readonly mentions: readonly string[];
  readonly onAdd: (userId: string) => void;
  readonly onRemove: (userId: string) => void;
  /** The whole control, while the comment it belongs to is in flight. */
  readonly disabled?: boolean;
  readonly t: Translate<'tasks'>;
}

export function MentionPicker({
  members,
  owningWorkspaceId,
  mentions,
  onAdd,
  onRemove,
  disabled = false,
  t,
}: MentionPickerProps) {
  const [pending, setPending] = useState<PendingMention | null>(null);

  const personName = (member: ProjectMember): string =>
    member.display_name ?? member.email ?? t('person.unavailable');

  const firmName = (member: ProjectMember): string =>
    member.company_name ?? t('audience.firmUnknown');

  /**
   * The same test `admissionFor` makes: a claimed company that is not the owner's. A member
   * with no company is nobody's outsider, and with no owning workspace in hand this screen
   * declines to guess.
   */
  const isExternal = (member: ProjectMember): boolean => {
    const firmId = member.company_workspace_id ?? null;
    return owningWorkspaceId !== null && firmId !== null && firmId !== owningWorkspaceId;
  };

  const labelFor = (member: ProjectMember): string =>
    isExternal(member)
      ? t('mention.external', { name: personName(member), firm: firmName(member) })
      : personName(member);

  const memberOf = (userId: string): ProjectMember | undefined =>
    members.find((member) => member.user_id === userId);

  return (
    <div className={styles.mentionRow}>
      <span className={styles.fieldLabel}>{t('mention.legend')}</span>
      <ChipSelect
        size="sm"
        disabled={disabled}
        tokens={mentions.map((userId) => {
          const member = memberOf(userId);
          // A mention held for somebody who has since left the project still renders and
          // stays removable — leaving did not un-name them.
          return { id: userId, label: member === undefined ? t('person.unavailable') : labelFor(member) };
        })}
        options={members
          .filter((member) => !mentions.includes(member.user_id))
          .map((member) => ({ id: member.user_id, label: labelFor(member) }))}
        addLabel={t('mention.add')}
        removeLabel={(name) => t('mention.remove', { name })}
        onAdd={(userId) => {
          const member = memberOf(userId);
          if (member === undefined) return;
          if (!isExternal(member)) {
            onAdd(userId);
            return;
          }
          // The act waits for the person: §F's sentence is stated first, and only a confirm
          // performs it.
          setPending({
            userId,
            personName: personName(member),
            firmName: firmName(member),
          });
        }}
        onRemove={(userId) => {
          // Removing what a pending sentence is about would leave the sentence talking about
          // nothing; the two are the same set as far as the reader is concerned.
          if (pending?.userId === userId) setPending(null);
          onRemove(userId);
        }}
      />

      {pending !== null ? (
        <div className={styles.notice}>
          <p className={styles.noticeText}>
            {t('mention.grantsAccess', { name: pending.personName, firm: pending.firmName })}
          </p>
          <div className={styles.noticeActions}>
            <Button
              size="sm"
              variant="primary"
              disabled={disabled}
              onClick={() => {
                onAdd(pending.userId);
                setPending(null);
              }}
            >
              {t('mention.confirm')}
            </Button>
            <Button
              size="sm"
              variant="tertiary"
              onClick={() => {
                setPending(null);
              }}
            >
              {t('label.cancel')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
